import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { useState, useEffect } from "react";

export const Route = createFileRoute("/_authenticated/admin/settings")({
  component: AdminSettingsPage,
});

function AdminSettingsPage() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Record<string, { en: string; ar: string }>>({});

  const { data: settings, isLoading } = useQuery({
    queryKey: ["admin-site-settings"],
    queryFn: async () => {
      const { data, error } = await supabase.from("site_settings").select("*");
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (settings) {
      const initial: Record<string, { en: string; ar: string }> = {};
      settings.forEach((s) => {
        initial[s.key] = { en: s.value_en || "", ar: s.value_ar || "" };
      });
      setFormData(initial);
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: async (updates: { key: string; value_en: string; value_ar: string }[]) => {
      const { error } = await supabase.from("site_settings").upsert(updates);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Settings saved successfully");
      queryClient.invalidateQueries({ queryKey: ["admin-site-settings"] });
      queryClient.invalidateQueries({ queryKey: ["site-settings"] }); // Invalidates the public one too
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to save settings");
    },
  });

  const handleSave = () => {
    const updates = Object.entries(formData).map(([key, vals]) => ({
      key,
      value_en: vals.en,
      value_ar: vals.ar,
    }));
    saveMutation.mutate(updates);
  };

  const handleFieldChange = (key: string, lang: "en" | "ar", value: string) => {
    setFormData((prev) => ({
      ...prev,
      [key]: { ...prev[key], [lang]: value },
    }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const groups = [
    {
      title: "Contact Information",
      keys: ["contact_email", "contact_phone", "contact_whatsapp", "contact_address"],
    },
    {
      title: "Social Media",
      keys: ["social_facebook", "social_instagram", "social_twitter", "social_youtube"],
    },
  ];

  return (
    <div className="space-y-6 max-w-4xl pb-12">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold">Site Settings</h1>
          <p className="text-muted-foreground mt-1">
            Manage global website configuration and contact details.
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saveMutation.isPending}
          className="rounded-full px-6"
        >
          {saveMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save Changes
        </Button>
      </div>

      {groups.map((group) => (
        <Card key={group.title} className="rounded-2xl border-0 shadow-soft">
          <CardHeader>
            <CardTitle>{group.title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {group.keys.map((key) => {
              const settingDef = settings?.find((s) => s.key === key);
              return (
                <div
                  key={key}
                  className="grid md:grid-cols-2 gap-4 border-b border-border/50 pb-6 last:border-0 last:pb-0"
                >
                  <div className="md:col-span-2">
                    <Label className="text-base font-semibold">
                      {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Label>
                    {settingDef?.description && (
                      <p className="text-sm text-muted-foreground">{settingDef.description}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase">English Value</Label>
                    <Input
                      value={formData[key]?.en ?? ""}
                      onChange={(e) => handleFieldChange(key, "en", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase" dir="rtl">
                      القيمة بالعربية
                    </Label>
                    <Input
                      value={formData[key]?.ar ?? ""}
                      onChange={(e) => handleFieldChange(key, "ar", e.target.value)}
                      dir="rtl"
                    />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
